import React, { useMemo } from 'react';
import styles from './Activity.module.scss';
import { Table } from 'new/elements/Table';
import { Column } from 'new/elements/Table/types';
import LoadableMonacoEditor from 'js/components/elements/editors/monaco/LoadableMonacoEditor';
import LoadableMonacoDiff from 'js/components/elements/editors/monaco/LoadableMonacoDiff';
import { Box } from 'new/elements/Box';
import { SourceStatus } from 'new/elements/SourceStatus';
import { Stack } from 'new/elements/Stack';
import { Typography } from '@mui/material';

type Change = {
  fromValue: any;
  toValue: any;
  key: string;
  keyDisplay: string;
  isDiffEditor: boolean;
};

type ChangesProps = {
  objectChanges: Change[];
  className?: string;
  event: string;
};

const isEmptyValue = (value: any): boolean => value === null || value === undefined || value === '';

const Value = (change: Change & { event: string; fieldName: string; isDiff: boolean }) => {
  const { event, fieldName, keyDisplay, isDiffEditor } = change;
  const fieldValue = change[fieldName];

  if (event === 'create') {
    if (isDiffEditor) {
      return (
        <LoadableMonacoEditor
          language="json"
          withBorders
          value={fieldValue}
          height={300}
          options={{
            readOnly: true,
            minimap: { enabled: false },
          }}
        />
      );
    }
    return isEmptyValue(fieldValue) ? '-' : fieldValue;
  }

  if (keyDisplay === 'Status') {
    return <SourceStatus status={fieldValue} />;
  }

  return isEmptyValue(fieldValue) ? '-' : fieldValue;
};

export const Changes = ({ objectChanges, event, className }: ChangesProps) => {
  // Separate diff editor changes from regular changes
  const diffChanges = objectChanges.filter((c) => c.isDiffEditor);
  const tableChanges = objectChanges.filter((c) => !c.isDiffEditor);

  const columns: Column<Change>[] = useMemo(
    () =>
      [
        {
          name: 'Property',
          field: 'keyDisplay',
          width: event === 'create' ? 30 : 30,
        },
        event !== 'create'
          ? {
              name: 'from',
              field: 'fromValue',
              width: 35,
              renderRow: (change) => {
                const { key, ...changeProps } = change;
                return (
                  <Value {...changeProps} event={event} fieldName="fromValue" isDiff={false} />
                );
              },
            }
          : null,
        {
          name: 'to',
          columnClassName: styles.columnWide,
          field: 'toValue',
          width: event === 'create' ? 70 : 35,
          renderRow: (change) => {
            const { key, ...changeProps } = change;
            return <Value {...changeProps} event={event} fieldName="toValue" isDiff={false} />;
          },
        },
      ].filter(Boolean) as Column<Change>[],
    [event],
  );

  return (
    <Box className={className}>
      {/* Table - only show non-diff changes */}
      {tableChanges.length > 0 && (
        <Table disableVirtualization columns={columns} rows={tableChanges} />
      )}

      {/* Config diffs below table */}
      {diffChanges.length > 0 && (
        <Stack mt="16px" gap="8px">
          {diffChanges.map((change) => {
            // For create events, show single Monaco editor with the created value
            if (event === 'create') {
              const value = change.toValue;
              const valueString =
                typeof value === 'string' ? value : JSON.stringify(value, null, 2);

              return (
                <Box
                  key={change.key}
                  className={styles.monacoDiffWrapper}
                  style={{ width: '100%' }}
                >
                  <Typography variant="bodySmall" className={styles.monacoDiffTitle}>
                    {change.keyDisplay}
                  </Typography>
                  <div style={{ border: '1px solid var(--color-greyscale-100)' }}>
                    <LoadableMonacoEditor
                      language="json"
                      withBorders
                      value={valueString}
                      height={300}
                      options={{
                        readOnly: true,
                        minimap: { enabled: false },
                        wordWrap: 'on',
                        lineNumbersMinChars: 6,
                      }}
                    />
                  </div>
                </Box>
              );
            }

            // For update/delete events, show diff view
            // Handle null/undefined values - show empty object instead of "null" string
            // This makes the diff more readable and ensures Monaco can properly highlight differences
            const normalizeValue = (value: any): any => {
              if (value === null || value === undefined || value === 'null') {
                return {};
              }
              return value;
            };

            const originalValue = normalizeValue(change.fromValue);
            const modifiedValue = normalizeValue(change.toValue);

            const originalString =
              typeof originalValue === 'string'
                ? originalValue
                : JSON.stringify(originalValue, null, 2);
            const modifiedString =
              typeof modifiedValue === 'string'
                ? modifiedValue
                : JSON.stringify(modifiedValue, null, 2);

            return (
              <Box key={change.key} className={styles.monacoDiffWrapper} style={{ width: '100%' }}>
                <Typography variant="bodySmall" className={styles.monacoDiffTitle}>
                  {change.keyDisplay}
                </Typography>
                <LoadableMonacoDiff
                  language="json"
                  original={originalString}
                  modified={modifiedString}
                  height={300}
                  options={{
                    readOnly: true,
                    renderSideBySide: true,
                    useInlineViewWhenSpaceIsLimited: true,
                    renderIndicators: true,
                    ignoreTrimWhitespace: false,
                    enableSplitViewResizing: true,
                    wordWrap: 'on',
                    lineNumbersMinChars: 6,
                    lineDecorationsWidth: 30,
                  }}
                />
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};
